import {
  useMediaQuery,
  useTextValidator,
} from '@ace-clublabs/shared-component-library';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SearchResult } from '@/components/shared/SearchBox';
import type { Destination } from '@/features/destinations/utils/Destination';
import { getDestinationRegionLabel } from '@/features/destinations/utils/DestinationRegionLabelMapper';
import { useDebounce } from '@/hooks/useDebounce';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import useAppointmentStore from '@/store/AppointmentStore';
import { FlowType } from '@/types/FlowType';
import { formatSearchedDestination } from '@/utils/formatSearchedDestination';
import {
  getRelevanceSearch,
  relevanceSearchQueryKey,
  useRelevanceSearch,
} from '../api/getRelevanceSearch';
import type { RSSearchBoxProps } from '../components/types';
import type { RelevanceSearchResult } from '../types/RelevanceSearchResult';
import {
  FALLBACK_DESTINATION_NAME,
  findDestinationByName,
  matchesUndecidedKeywords,
  resolveDestinationFromSearchInput,
  UNDECIDED_OPTION_ID,
} from '../utils/resolveRelevanceSearchDestination';

const buildSearchResults = (
  query: string,
  relevanceResults: RelevanceSearchResult[] | undefined,
  undecidedOption: SearchResult,
): SearchResult[] | undefined => {
  if (matchesUndecidedKeywords(query)) {
    return [undecidedOption];
  }

  if (!relevanceResults) return;
  if (!relevanceResults.length) {
    if (!query) return;
    return [];
  }

  return relevanceResults.map(mapToSearchResult);
};

const mapToSearchResult = (result: RelevanceSearchResult): SearchResult => {
  const isCountry = result.type?.includes('country');
  const isContinent = result.country === 'Unknown';

  return {
    id: result.id,
    title: result.name,
    icon: 'location-dot-duotone',
    subtitle:
      isCountry || isContinent
        ? undefined
        : [result.region, result.country].filter(Boolean).join(', '),
  };
};

export interface UseRSSearchBoxOptions {
  flow: FlowType;
  formVariant?: boolean;
  proceedWithDestination?: (destination: Destination) => void;
}

export const useRSSearchBox = ({
  flow,
  formVariant = false,
  proceedWithDestination,
}: UseRSSearchBoxOptions) => {
  const [search, setSearch] = useState('');
  const [selectedResult, setSelectedResult] = useState<
    RelevanceSearchResult | undefined
  >();
  const [showWarning, setShowWarning] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false);

  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('md'));

  useEffect(() => {
    if (!isMobileView) {
      setIsSearchPanelOpen(false);
    }
  }, [isMobileView]);

  const useGoogleApiForRBS = useFeatureFlag('useGoogleApiForRBS');

  const setSelectedDestination = useAppointmentStore(
    (state) => state.setSelectedDestination,
  );
  const setSearchedDestination = useAppointmentStore(
    (state) => state.setSearchedDestination,
  );
  const setUserSearch = useAppointmentStore((state) => state.setUserSearch);
  const destinations = useAppointmentStore((state) => state.destinations);

  const queryClient = useQueryClient();
  const debouncedSearch = useDebounce(search, 300);
  const relevanceSearchQuery = useRelevanceSearch(debouncedSearch);

  const undecidedOption: SearchResult = useMemo(
    () => ({
      id: UNDECIDED_OPTION_ID,
      title: `I don't know, I haven't picked a destination yet.`,
      icon: 'circle-question-regular',
    }),
    [],
  );

  const validator = useTextValidator({
    label: 'Destination',
    preValidationMutators: [
      ({ value }) => {
        if (searchBoxProps.selectedResult) {
          return searchBoxProps.selectedResult.title;
        }
        return value;
      },
    ],
    validations: [
      ({ value }) => {
        if (!value?.trim()) {
          setShowWarning(true);
          return {
            value: value,
            isValid: false,
            message: 'Please select a destination',
          };
        }
        setShowWarning(false);
        return {
          value: value,
          isValid: true,
        };
      },
    ],
    postValidationMutators: [
      ({ value }) => (searchBoxProps.selectedResult ? '' : value),
    ],
    required: false,
    validationFeedback: 'delayed',
  });

  const searchResults: SearchResult[] | undefined = useMemo(
    () =>
      buildSearchResults(
        debouncedSearch,
        relevanceSearchQuery.data,
        undecidedOption,
      ),
    [relevanceSearchQuery.data, debouncedSearch, undecidedOption],
  );

  const selectedSearchResult = useMemo(
    () => (selectedResult ? mapToSearchResult(selectedResult) : undefined),
    [selectedResult],
  );

  const resetSearchState = useCallback(() => {
    setSearch('');
    setSelectedResult(undefined);
    setShowWarning(false);
    setHasInteracted(false);
    setIsSearchPanelOpen(false);
  }, []);

  useEffect(() => () => resetSearchState(), [resetSearchState]);

  const handleSearchChange = useCallback((searchString: string) => {
    setSearch(searchString);
    if (searchString.trim()) {
      setSelectedResult(undefined);
    }
    setShowWarning(false);
  }, []);

  const proceedWithFallbackDestination = useCallback(() => {
    const fallbackDestination = findDestinationByName(
      destinations,
      FALLBACK_DESTINATION_NAME,
    );

    if (!fallbackDestination) return;

    setSelectedDestination(fallbackDestination);
    proceedWithDestination?.(fallbackDestination);
  }, [
    destinations,
    setSelectedDestination,
    proceedWithDestination,
  ]);

  const handleSelectResult = useCallback(
    (result: SearchResult | null) => {
      if (!result) {
        setSelectedResult(undefined);
        return;
      }

      if (result.id === undecidedOption.id) {
        proceedWithFallbackDestination();
        return;
      }

      const relevanceResult = relevanceSearchQuery.data?.find(
        (entry) => entry.id === result.id,
      );

      if (!relevanceResult) return;
      setSelectedResult(relevanceResult);

      const selectedDestination = destinations.find(
        (dest) => dest.name === relevanceResult.aceDestination,
      );

      if (!selectedDestination) return;

      const regionLabel = getDestinationRegionLabel(selectedDestination.name);

      const mappedDestination: Destination = {
        ...selectedDestination,
        name: regionLabel,
      };

      const searchedDestinationString = formatSearchedDestination([
        relevanceResult.name,
        relevanceResult.region,
        relevanceResult.subRegion,
        relevanceResult.country,
      ]);

      setSearchedDestination(searchedDestinationString);
      setSelectedDestination(mappedDestination);
      proceedWithDestination?.(mappedDestination);
    },
    [
      relevanceSearchQuery.data,
      proceedWithFallbackDestination,
      undecidedOption.id,
      destinations,
      setSearchedDestination,
      setSelectedDestination,
      proceedWithDestination,
    ],
  );

  const resolveDestination = useCallback(
    () =>
      resolveDestinationFromSearchInput({
        typedSearch: search,
        selectedResult,
        relevanceResults: relevanceSearchQuery.data ?? [],
        searchResults: searchResults ?? [],
        destinations,
        undecidedOptionId: undecidedOption.id,
      }),
    [
      search,
      selectedResult,
      relevanceSearchQuery.data,
      searchResults,
      destinations,
      undecidedOption.id,
    ],
  );

  const handleUnmatchedDestination = useCallback(() => {
    const trimmedSearch = search.trim();
    if (!trimmedSearch) return;
    setUserSearch(trimmedSearch);
    setSearchedDestination('');
    proceedWithFallbackDestination();
  }, [
    search,
    proceedWithFallbackDestination,
    setUserSearch,
    setSearchedDestination,
  ]);

  const handleClickContinue = useCallback(async () => {
    if (!selectedResult && !search.trim()) {
      setShowWarning(true);
      return;
    }

    setShowWarning(false);

    const trimmedSearch = search.trim();
    let relevanceResults = relevanceSearchQuery.data ?? [];
    let resolvedSearchResults = searchResults ?? [];

    if (
      trimmedSearch &&
      trimmedSearch !== debouncedSearch.trim() &&
      !selectedResult
    ) {
      relevanceResults = await queryClient.fetchQuery({
        queryKey: relevanceSearchQueryKey(
          trimmedSearch,
          useGoogleApiForRBS,
        ),
        queryFn: () =>
          getRelevanceSearch(
            trimmedSearch,
            useGoogleApiForRBS,
          ),
        staleTime: Number.POSITIVE_INFINITY,
      });
      resolvedSearchResults =
        buildSearchResults(trimmedSearch, relevanceResults, undecidedOption) ??
        [];
    }

    const resolvedDestination = resolveDestinationFromSearchInput({
      typedSearch: trimmedSearch,
      selectedResult,
      relevanceResults,
      searchResults: resolvedSearchResults,
      destinations,
      undecidedOptionId: undecidedOption.id,
    });

    if (!resolvedDestination) return;

    const { selectedDestination, searchedDestination, userSearch } =
      resolvedDestination;

    setUserSearch(userSearch ?? '');
    setSearchedDestination(searchedDestination);
    setSelectedDestination(selectedDestination);
    proceedWithDestination?.(selectedDestination);
  }, [
    selectedResult,
    search,
    debouncedSearch,
    proceedWithDestination,
    setSelectedDestination,
    setSearchedDestination,
    setUserSearch,
    relevanceSearchQuery.data,
    searchResults,
    queryClient,
    useGoogleApiForRBS,
    undecidedOption,
    destinations,
  ]);

  const handleSearchPanelOpenChange = useCallback((open: boolean) => {
    setIsSearchPanelOpen(open);
  }, []);

  const handleBlur = useCallback(() => {
    setHasInteracted(true);
    setIsSearchPanelOpen(false);
  }, []);

  const handleFocus = useCallback(() => {
    setIsSearchPanelOpen(true);
  }, []);

  const showWarningState = formVariant
    ? hasInteracted && showWarning
    : showWarning;

  const searchBoxProps: RSSearchBoxProps = useMemo(() => {
    const placeholder = showWarningState
      ? 'Please type in a destination.'
      : 'Where would you like to travel?';

    return {
      placeholder,
      showWarning: showWarningState,
      onSelect: handleSelectResult,
      selectedResult: selectedSearchResult,
      value: search,
      onChange: handleSearchChange,
      onBlur: handleBlur,
      searchResults,
      isLoading: relevanceSearchQuery.isLoading,
      onOpenChange: handleSearchPanelOpenChange,
      ...(flow !== FlowType.EMAIL && { onContinueClick: handleClickContinue }),
      validator,
    };
  }, [
    showWarningState,
    handleSelectResult,
    selectedSearchResult,
    search,
    handleSearchChange,
    handleBlur,
    searchResults,
    relevanceSearchQuery.isLoading,
    flow,
    handleClickContinue,
    handleSearchPanelOpenChange,
    validator,
  ]);

  return {
    search,
    selectedResult,
    selectedSearchResult,
    showWarning,
    showWarningState,
    isSearchPanelOpen,
    searchResults,
    isLoading: relevanceSearchQuery.isLoading,
    handleSearchChange,
    handleSelectResult,
    handleSearchPanelOpenChange,
    handleBlur,
    handleFocus,
    handleClickContinue,
    handleUnmatchedDestination,
    searchBoxProps,
    resolveDestination,
  };
};
